import { useEffect, useRef } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { isSsoSignedOutRecently, useSsoAuth } from '@wehunt/core-web'
import { AuthShell, AuthHeader } from '@wehunt/ui'

/**
 * What `/sign-in` and `/sign-up` are in sso mode: a hand-off, not a form.
 *
 * The navigation starts on mount, but the button stays on screen rather than
 * being a failure state. Building the authorize URL is asynchronous (the PKCE
 * challenge is a hash), a browser may refuse a programmatic navigation, and
 * the back button lands the user right back here -- in every one of those
 * cases a visible way forward beats a spinner.
 *
 * The one visit that must NOT start a login is the tail of a sign-out. This
 * page is reached twice on the way out: once when the local sign-out flips
 * the auth store, and again when sso returns the tab to its logoutRedirect.
 * An automatic start on either would race the navigation to sso's logout, or
 * simply sign the user back in through the session sso still holds.
 */
export function SsoRedirectPage() {
  const { startSsoLogin } = useSsoAuth()
  const { t } = useTranslation('app')
  const { returnTo } = useSearch({ strict: false }) as { returnTo?: string }

  // Read once per mount rather than once per render: the same answer decides
  // what this page says and whether the effect below starts a login, and the
  // two must not disagree. The marker is a pure read that outlives this page
  // load, so a sign-out that loads the page again is suppressed again.
  const signedOut = useRef<boolean | undefined>(undefined)
  signedOut.current ??= isSsoSignedOutRecently()

  const start = () => {
    startSsoLogin(returnTo).catch((err: unknown) => {
      // The Continue button below is the recovery path, so this only has to
      // reach the console for a support investigation.
      console.error('[SsoRedirectPage] starting the sso login failed', err)
    })
  }

  // One decision per mount: React's StrictMode double-invokes mount effects
  // in development, and starting a login twice would leave two half-started
  // logins in sessionStorage and hand the browser two navigations. The marker
  // itself does not need protecting -- it is a timestamp that no read
  // consumes, cleared only when a login actually starts.
  const decided = useRef(false)

  useEffect(() => {
    if (decided.current) return
    decided.current = true
    if (signedOut.current) return
    start()
  }, [])

  return (
    <AuthShell>
      <AuthHeader
        logoSrc="/assets/icon-192.png"
        logoAlt="WeHunt"
        align="center"
        title={signedOut.current ? t('sso.signedOut') : t('sso.redirecting')}
        subtitle={t('signIn.subtitle')}
      />
      <button
        type="button"
        className="wh-btn wh-btn-primary"
        onClick={start}
        style={{ height: 52, width: '100%', borderRadius: 12, fontSize: 16, fontWeight: 700 }}
      >
        {t('sso.continueButton')}
      </button>
    </AuthShell>
  )
}
